// src/components/MsoCalculationDetail.tsx
//
// ★★ ANNEX A 22 — ONE stored calculation, as the enquiry shows it: what was asked, what the engine
// answered, how it got there, and the exact rule version it used.
//
// ⚠ **The rule shown is the version the calculation RECORDED, not the one in force today.** A
// calculation dated inside a superseded window resolves to the older version and keeps naming it
// after a newer one activates (Review Focus 4), so this screen reads `version` from the route's
// own join on `rule_version_id` and never looks up "the current rule" by key.
//
// ⚠ The stored input is read as a bill (labelled fields, amounts in dollars) by `MsoInputSummary`, and the
// exact JSON the engine received, in whole cents, stays one disclosure away: that is the record, and a
// re-formatted copy alone would be a second account of it.
import { Link } from 'react-router'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useCrumbTitle } from '../lib/crumbs'
import { formatInstant, formatIsoDate } from '../lib/format'
import { msoRefusalLabel } from '../lib/mso-vocabulary'
import { DefinitionView } from './MsoDefinitionView'
import { MsoTrace } from './MsoTrace'
import { MsoInputSummary } from './MsoInputSummary'
import { MsoAuditEvidence } from './MsoAuditEvidence'
import { Money } from './Money'
import { CategoryBadge } from './StatusBadge'
import { ErrorCard, LoadingCard } from './StateCards'

export function MsoCalculationDetail({ id }: { id: string }) {
  const detail = useApi(() => api.msoCalculation(id), [id])
  // The trail and the tab name the calculation by the start of its reference, not "This record".
  const notFound = detail.error !== null && (detail.errorStatus === 404 || detail.errorStatus === 400 || /no such calculation/i.test(detail.error))
  useCrumbTitle(detail.error === null ? `Calculation ${id.slice(0, 8)}` : notFound ? 'Calculation not found' : null)

  if (detail.error !== null) {
    // ★ F-29: a reference that matches nothing is "no such calculation", with a heading and the way back. Retry is
    // pointless for it (the answer will not change), so it is not offered.
    if (notFound) {
      return (
        <div className="pt-card flex flex-col gap-3" role="alert">
          <h1 className="text-2xl m-0">No such calculation</h1>
          <p className="m-0 max-w-[70ch]">
            PRS2.0 holds no recorded calculation with the reference <span className="pt-mono [overflow-wrap:anywhere]">{id}</span>. Check the
            reference, or find the calculation in the enquiry.
          </p>
          <p className="m-0">
            <Link to="/mso/calculations">Back to the calculation enquiry</Link>
          </p>
        </div>
      )
    }
    return <ErrorCard title="Could not load this calculation" message={detail.error} {...(detail.retryable && { onRetry: detail.reload })} />
  }
  if (detail.initialLoading || detail.data === undefined) return <LoadingCard label="Loading the calculation" rows={4} />

  const { calculation, version } = detail.data
  const refused = calculation.outcome === 'REFUSED'

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl mb-2">MSO calculation</h1>
          <p className="pt-mono text-sm text-[var(--sgds-body-color-subtle)]">{calculation.id}</p>
        </div>
        <Link to="/mso/calculations" className="text-sm pt-link-target">
          Back to the calculation enquiry
        </Link>
      </div>

      <section className="pt-card" aria-label="Outcome">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
          <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Computed</dt>
          <dd>{formatInstant(calculation.computedAt)}</dd>
          <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Outcome</dt>
          <dd className="font-semibold">
            {refused ? (
              <span className="inline-flex flex-wrap items-center gap-1">
                <CategoryBadge kind="outcome" value="REFUSED" label="Refused" />
                <span className="sr-only">: </span>
                <CategoryBadge kind="refusal" value={calculation.refusalKind ?? 'UNKNOWN'} label={msoRefusalLabel(calculation.refusalKind ?? 'UNKNOWN')} />
              </span>
            ) : (
              <CategoryBadge kind="outcome" value="CALCULATED" label="Calculated" />
            )}
          </dd>
          <dt className="text-sm font-semibold">Officer or dependant pays</dt>
          <dd>
            <Money cents={calculation.officerCents} placeholder="No amount (refused)" />
          </dd>
          <dt className="text-sm font-semibold">Government pays</dt>
          <dd>
            <Money cents={calculation.governmentCents} placeholder="No amount (refused)" />
          </dd>
          <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Rule used</dt>
          <dd className="pt-mono">
            {version === null || calculation.ruleVersionId === null ? (
              'None selected'
            ) : (
              <Link to={`/administration/mso-rules/versions/${calculation.ruleVersionId}`}>
                {version.ruleKey} v{version.version}
              </Link>
            )}
          </dd>
          <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Computed by</dt>
          <dd className="text-sm">{calculation.computedByName ?? (calculation.computedBy === null ? 'Not recorded' : 'Name not available')}</dd>
        </dl>
      </section>

      <MsoTrace trace={calculation.trace} heading="The recorded trace" level={2} />

      <section className="pt-card" aria-label="Stored input">
        <h2 className="text-lg font-semibold mb-1">What was asked</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] mb-3 max-w-[75ch]">
          The bill the engine was given, as it received it.
        </p>
        <MsoInputSummary input={calculation.input} />
      </section>

      <MsoAuditEvidence
        scopes={[{ resourceType: 'mso-calculation', resourceId: calculation.id }]}
        intro="The audit events recorded for this calculation, newest first."
      />

      {version !== null && (
        <section className="pt-card" aria-label="Rule version used">
          <h2 className="text-lg font-semibold mb-1">
            The rule it used: {version.ruleKey} version {version.version}
          </h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)] mb-3">
            In force from {formatIsoDate(version.effectiveFrom)}
            {version.effectiveUntil === null ? '' : ` until ${formatIsoDate(version.effectiveUntil)}`}. This is the definition as
            it stood when the calculation ran.
          </p>
          <DefinitionView definition={version.definition} />
        </section>
      )}
    </div>
  )
}
